/**
 * Authentication Slice
 * Manages authentication state and actions
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import {
  login as loginApi,
  logout as logoutApi,
  register as registerApi,
  refreshToken as refreshTokenApi,
  googleProviderToken,
  requestMagicLink as requestMagicLinkApi,
  verifyMagicLink as verifyMagicLinkApi,
} from "@/services/api/endpoints/auth.api";
import { getTeacherProfile } from "@/services/api/endpoints/teacher-profile.api";
import {
  setSessionToken,
  clearSessionToken,
  clearAllStorage,
  getSessionToken,
} from "@/utils/storage";
import { AuthState } from "@/types/store.types";
import { clearProfile } from "./teacherProfileSlice";
import {
  LoginCredentials,
  RegisterCredentials,
  LoginResponse,
  GoogleLoginRequest,
  DjangoUser,
  MagicLinkRequestPayload,
  MagicLinkVerifyPayload,
  MagicLinkStatus,
} from "@/types/auth.types";
import { logger } from "@/utils/logger";

/**
 * Initial state
 */
const initialState: AuthState = {
  isAuthenticated: false,
  sessionToken: null,
  user: null,
  loading: false,
  error: null,
  // Magic Link state
  magicLinkStatus: "idle" as MagicLinkStatus,
  magicLinkError: null,
  maskedEmail: null,
  // Login tracking
  justLoggedIn: false,
};

/**
 * Async Thunks
 */

/**
 * Login user
 */
export const login = createAsyncThunk(
  "auth/login",
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response: LoginResponse = await loginApi(credentials);
      setSessionToken(response.sessionToken);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

/**
 * Register new user
 */
export const register = createAsyncThunk(
  "auth/register",
  async (credentials: RegisterCredentials, { rejectWithValue }) => {
    try {
      const response: LoginResponse = await registerApi(credentials);
      setSessionToken(response.sessionToken);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Registration failed");
    }
  }
);

/**
 * Logout user
 */
export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await logoutApi();

      // Limpar storages
      clearSessionToken();
      clearAllStorage();

      // ⚠️ CRÍTICO: Limpar perfil do Redux
      dispatch(clearProfile());
    } catch (error: any) {
      // Ainda limpar dados locais mesmo se API falhar
      clearSessionToken();
      clearAllStorage();

      // ⚠️ CRÍTICO: Limpar perfil mesmo em caso de erro
      dispatch(clearProfile());

      return rejectWithValue(error.message || "Logout failed");
    }
  }
);

/**
 * Verify authentication by fetching current user
 */
export const verifyAuth = createAsyncThunk(
  "auth/verify",
  async (_, { rejectWithValue }) => {
    try {
      const profile = await getTeacherProfile();
      // Map TeacherProfile to User
      const user: any = {
        ...profile,
        id: (profile as any).id || 0, // TeacherProfile might not have ID at top level depending on schema
        display: profile.name,
        username: profile.email,
      };
      return user;
    } catch (error: any) {
      clearSessionToken();
      return rejectWithValue(error.message || "Authentication failed");
    }
  }
);

/**
 * Refresh session token
 */
export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const response: LoginResponse = await refreshTokenApi();
      setSessionToken(response.sessionToken);
      return response;
    } catch (error: any) {
      clearSessionToken();
      return rejectWithValue(error.message || "Token refresh failed");
    }
  }
);

/**
 * Google Social Login (Django Allauth Headless)
 */
export const googleLogin = createAsyncThunk(
  "auth/googleLogin",
  async (request: GoogleLoginRequest, { rejectWithValue }) => {
    try {
      const response = await googleProviderToken(request);

      logger.log("🔍 [AuthSlice] Google Login Response:", {
        hasData: !!response.data,
        hasUser: !!response.data?.user,
        isAdmin: response.data?.user?.is_admin,
        keys: Object.keys(response.data?.user || {}),
      });

      // Store session token
      setSessionToken(response.meta.session_token);

      // Initial mapping from login response
      const backendUser = response.data.user;
      let user: any = {
        id: backendUser.id,
        email: backendUser.email,
        display: backendUser.display,
        username: backendUser.username,
        has_usable_password: backendUser.has_usable_password,
        is_admin: backendUser.is_admin,
        name: backendUser.name || backendUser.display,
        avatar: backendUser.photo,
      };

      // ⚠️ CRITICAL: Fetch full user profile to ensure we have all fields (like is_admin)
      // The Google Login response might contain a partial user object
      try {
        const userProfile = await getTeacherProfile();
        logger.log("🔍 [AuthSlice] Fetched Full User Profile:", {
          email: userProfile.email,
          isAdmin: userProfile.is_admin,
        });

        // Update user with full profile data
        user = {
          ...user,
          ...userProfile,
          // Ensure is_admin is preserved/updated
          is_admin: userProfile.is_admin,
        };
      } catch (err) {
        logger.warn(
          "⚠️ [AuthSlice] Failed to fetch full user profile, using login response data",
          err
        );
      }

      return {
        sessionToken: response.meta.session_token,
        accessToken: response.meta.access_token,
        isAuthenticated: response.meta.is_authenticated,
        user,
        methods: response.data.methods,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Google login failed");
    }
  }
);

/**
 * Request Magic Link (Passwordless Authentication)
 */
export const requestMagicLink = createAsyncThunk(
  "auth/requestMagicLink",
  async (payload: MagicLinkRequestPayload, { rejectWithValue }) => {
    try {
      const response = await requestMagicLinkApi(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to send magic link");
    }
  }
);

/**
 * Verify Magic Link Code (Passwordless Authentication)
 */
export const verifyMagicLink = createAsyncThunk(
  "auth/verifyMagicLink",
  async (payload: MagicLinkVerifyPayload, { rejectWithValue }) => {
    try {
      const response = await verifyMagicLinkApi(payload);

      // Store session token
      setSessionToken(response.meta.session_token);

      return {
        sessionToken: response.meta.session_token,
        isAuthenticated: response.meta.is_authenticated,
        user: response.data.user,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to verify magic link");
    }
  }
);

/**
 * Auth Slice
 */
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Clear error message
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set session token (for manual updates)
     */
    setToken: (state, action: PayloadAction<string>) => {
      state.sessionToken = action.payload;
      state.isAuthenticated = true;
      setSessionToken(action.payload);
    },

    /**
     * Reset auth state
     */
    resetAuth: (state) => {
      Object.assign(state, initialState);
      clearSessionToken();
    },

    /**
     * Reset magic link state to idle
     */
    resetMagicLinkState: (state) => {
      state.magicLinkStatus = "idle";
      state.magicLinkError = null;
      state.maskedEmail = null;
    },

    /**
     * Clear just logged in flag
     */
    clearJustLoggedInFlag: (state) => {
      state.justLoggedIn = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.sessionToken = action.payload.sessionToken;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })

      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.sessionToken = action.payload.sessionToken;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })

      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        Object.assign(state, initialState);
      })
      .addCase(logout.rejected, (state) => {
        // Clear state even if logout fails
        Object.assign(state, initialState);
      })

      // Verify Auth
      .addCase(verifyAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(verifyAuth.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.sessionToken = null;
        state.error = action.payload as string;
      })

      // Refresh Token
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        state.sessionToken = action.payload.sessionToken;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.sessionToken = null;
        state.user = null;
        state.error = action.payload as string;
      })

      // Google Login
      .addCase(googleLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = action.payload.isAuthenticated;
        state.sessionToken = action.payload.sessionToken;
        state.user = action.payload.user as any; // Type assertion needed here
        state.error = null;
        state.justLoggedIn = true; // ⚠️ NOVO: Marcar login para forçar fetch
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })

      // Request Magic Link
      .addCase(requestMagicLink.pending, (state) => {
        state.magicLinkStatus = "requesting";
        state.magicLinkError = null;
        state.maskedEmail = null;
      })
      .addCase(requestMagicLink.fulfilled, (state, action) => {
        state.magicLinkStatus = "request-success";
        state.maskedEmail = action.payload.email;
        state.magicLinkError = null;
      })
      .addCase(requestMagicLink.rejected, (state, action) => {
        state.magicLinkStatus = "request-error";
        state.magicLinkError = action.payload as string;
      })

      // Verify Magic Link
      .addCase(verifyMagicLink.pending, (state) => {
        state.magicLinkStatus = "verifying";
        state.magicLinkError = null;
        state.loading = true;
      })
      .addCase(verifyMagicLink.fulfilled, (state, action) => {
        state.magicLinkStatus = "verify-success";
        state.loading = false;
        state.isAuthenticated = action.payload.isAuthenticated;
        state.sessionToken = action.payload.sessionToken;
        state.user = action.payload.user as any;
        state.magicLinkError = null;
        state.justLoggedIn = true; // ⚠️ NOVO: Marcar login para forçar fetch
      })
      .addCase(verifyMagicLink.rejected, (state, action) => {
        state.magicLinkStatus = "verify-error";
        state.magicLinkError = action.payload as string;
        state.loading = false;
      })

      // Handle redux-persist rehydration
      // Validate auth state after rehydration from localStorage
      .addCase(REHYDRATE as any, (state) => {
        // Check if session token actually exists in localStorage
        const savedToken = getSessionToken();

        // If Redux says user is authenticated but no token exists, clear auth state
        if (state.isAuthenticated && !savedToken) {
          state.isAuthenticated = false;
          state.sessionToken = null;
          state.user = null;
          state.error = null;
          clearSessionToken();
          clearAllStorage();
        }
      });
  },
});

export const {
  clearError,
  setToken,
  resetAuth,
  resetMagicLinkState,
  clearJustLoggedInFlag,
} = authSlice.actions;
export default authSlice.reducer;
